import { ThingDocument } from '@cco/model';
import { createReducer, on } from '@ngrx/store';
import * as lodash from 'lodash';
import * as IotThingActions from './iot-thing.actions';

/**
 * Mutates the state => Only use this on a deep cloned state
 */
function mergeToState(
  state: State,
  assetUuid: string,
  thingName: string,
  thingTypeName: string | undefined,
): State {
  const assetThingMap = state[assetUuid];

  if (!assetThingMap) {
    state[assetUuid] = {
      thingNames: [thingName],
      byThingType: {},
    };
    if (thingTypeName) {
      state[assetUuid].byThingType[thingTypeName] = [thingName];
    }
  } else {
    const { thingNames, byThingType } = assetThingMap;

    if (!thingNames.includes(thingName)) {
      thingNames.push(thingName);
    }
    if (thingTypeName) {
      if (!byThingType[thingTypeName]) {
        byThingType[thingTypeName] = [thingName];
      } else if (!byThingType[thingTypeName].includes(thingName)) {
        byThingType[thingTypeName].push(thingName);
      }
    }
  }

  return state;
}

function upsertThing(state: State, thing: ThingDocument): State {
  const assetUuid = thing.attributes?.assetUuid;
  const thingName = thing.thingName;
  const thingTypeName = thing.thingTypeName;

  if (
    !assetUuid ||
    (state[assetUuid]?.thingNames?.includes(thingName) &&
      (!thingTypeName || state[assetUuid]?.byThingType[thingTypeName]?.includes(thingName)))
  ) {
    return state;
  }

  const newState = lodash.cloneDeep(state);
  return mergeToState(newState, assetUuid, thingName, thingTypeName);
}

function upsertThings(state: State, things: ThingDocument[]): State {
  const newState = lodash.cloneDeep(state);

  things.forEach((thing) => {
    const assetUuid = thing.attributes?.assetUuid;
    const thingName = thing.thingName;
    const thingTypeName = thing.thingTypeName;

    if (
      !assetUuid ||
      (state[assetUuid]?.thingNames?.includes(thingName) &&
        (!thingTypeName || state[assetUuid]?.byThingType[thingTypeName]?.includes(thingName)))
    ) {
      return;
    }

    mergeToState(newState, assetUuid, thingName, thingTypeName);
  });

  return newState;
}

export interface AssetThingMap {
  thingNames: string[];
  byThingType: { [thingTypeName: string]: string[] };
}

export interface State {
  [assetUuid: string]: AssetThingMap;
}

export const reducer = createReducer<State>(
  {},
  on(IotThingActions.upsertThing, (state, action) => upsertThing(state, action.thing)),
  on(IotThingActions.upsertThings, (state, action) => upsertThings(state, action.things)),
);
